import { IColumn, IGroup, MessageBarType } from '@fluentui/react';
import { DetailsList, IconButton, Item, MessageBar, TooltipHost, useTheme } from '@h2oai/ui-kit';
import { useEffect, useMemo, useState } from 'react';

import {
  InstanceEventsRequest,
  InstanceEventsResponse,
} from '../../../../../admin-center/gen/app_event/instanceEvent/instanceevent_pb';
import { useAdminCenterService } from '../../../../../admin-center/hooks';
import { useUser } from '../../../../../utils/hooks';
import { formatDefaultDateAndTime } from '../../../../../utils/utils';
import {
  columnHeaderContainerStyles,
  columnStyles,
  customTableStyles,
  groupIconStyle,
  tooltipHostStyles,
} from '../../../common/common.styles';
import { buildUserContext, getDummyList } from '../../../common/utils';
import {
  InstanceEventName,
  InstanceEventOriginalName,
  InstanceEventTableColKey,
  InstanceEventTableColName,
  NA,
} from '../../../constants';
import SkeletonLoader from '../../SkeletonLoader/SkeletonLoader';
import { tableStyle } from '../AppEvent.styles';
import {
  resumedEventStyles,
  startedEventStyles,
  suspendedEventStyles,
  terminatedEventStyles,
  updatedEventStyles,
} from './InstanceTable.styles';

interface InstanceEvent {
  instanceName: string;
  instanceEvent: string;
  instanceUser: string;
  instanceDate: string;
  instanceTime: string;
}

interface InstanceEventTableProps {
  startDate: string;
  endDate: string;
  searchQuery: string;
}

const dummyList = getDummyList<InstanceEvent>(8);

export default function InstanceTable({ startDate, endDate, searchQuery }: InstanceEventTableProps) {
  const service = useAdminCenterService();
  const theme = useTheme();
  const user = useUser();
  const [instanceEventData, setInstanceEventData] = useState<InstanceEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sortColumn, setSortColumn] = useState<string | undefined>(undefined);
  const [isSortedDescending, setIsSortedDescending] = useState<boolean>(false);
  const [groupByKeys, setGroupByKeys] = useState<string[]>([]);

  const fetchInstanceEvents = async () => {
    setIsLoading(true);
    try {
      const userContext = await buildUserContext(user);
      const request: InstanceEventsRequest = {
        startDate: startDate,
        endDate: endDate,
        userContext: userContext,
      };

      const response: InstanceEventsResponse = await service.getInstanceEvents(request);

      const INSTANCE_EVENT_MAP: Record<string, string> = {
        [InstanceEventOriginalName.terminate]: InstanceEventName.terminate,
        [InstanceEventOriginalName.start]: InstanceEventName.start,
        [InstanceEventOriginalName.update]: InstanceEventName.update,
        [InstanceEventOriginalName.resume]: InstanceEventName.resume,
        [InstanceEventOriginalName.suspend]: InstanceEventName.suspend,
        nan: NA,
      };
      const getInstanceEventColumnEntry = (event: string | undefined): string => {
        if (!event) return NA;
        return INSTANCE_EVENT_MAP[event.toLowerCase()] || event;
      };

      const getInstanceTitleColumnEntry = (event: string | undefined): string => {
        if (!event) return NA;
        try {
          const parsedEvent = JSON.parse(event);
          if (parsedEvent && typeof parsedEvent === 'object' && 'appInstanceEvent' in parsedEvent) {
            const events = parsedEvent['appInstanceEvent'];
            const firstEventKey = Object.keys(events)[0];

            if (firstEventKey && events[firstEventKey] && events[firstEventKey]['instance']) {
              return events[firstEventKey]['app']['title'];
            }
          }
        } catch (e) {
          return NA;
        }
        return NA;
      };

      const formattedData = (response.events ?? []).map((event) => {
        const utcDate = event.ts ? new Date(event.ts) : null;

        return {
          instanceName: getInstanceTitleColumnEntry(event.label) ?? '-',
          instanceEvent: getInstanceEventColumnEntry(event.stream) ?? '-',
          instanceUser: event.userName ?? '-',
          instanceDate: formatDefaultDateAndTime(utcDate) || '-',
          instanceTime: utcDate
            ? utcDate.toLocaleTimeString('en-US', {
                hour12: false,
                timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
              })
            : '-',
        };
      });

      setInstanceEventData(formattedData);
    } catch (error) {
      console.error('Error fetching login events:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchInstanceEvents();
  }, [startDate, endDate]);

  const filteredData = useMemo(() => {
    const searchTerm = searchQuery.toLowerCase();
    let filtered = instanceEventData.filter(
      (item) =>
        item.instanceName.toLowerCase().includes(searchTerm) ||
        item.instanceEvent.toLowerCase().includes(searchTerm) ||
        item.instanceUser.toLowerCase().includes(searchTerm) ||
        item.instanceDate.toLowerCase().includes(searchTerm) ||
        item.instanceTime.toLowerCase().includes(searchTerm)
    );

    if (sortColumn) {
      filtered = [...filtered].sort((a, b) => {
        const valA = a[sortColumn as keyof InstanceEvent] as string;
        const valB = b[sortColumn as keyof InstanceEvent] as string;
        return isSortedDescending ? valA.localeCompare(valB) : valB.localeCompare(valA);
      });
    }

    return filtered;
  }, [instanceEventData, searchQuery, sortColumn, isSortedDescending]);

  const { groupedItems, groups } = useMemo(() => {
    if (groupByKeys.length === 0) return { groupedItems: filteredData, groups: undefined };

    const groupedMap = new Map<string, { items: InstanceEvent[]; keys: string[] }>();

    filteredData.forEach((item) => {
      const keyValues = groupByKeys.map((key) => String(item[key as keyof InstanceEvent]));
      const groupKey = keyValues.join('||');

      if (!groupedMap.has(groupKey)) {
        groupedMap.set(groupKey, { items: [], keys: keyValues });
      }

      groupedMap.get(groupKey)!.items.push(item);
    });

    const groupedItems: InstanceEvent[] = [];
    const groups: IGroup[] = [];

    let startIndex = 0;
    for (const [groupKey, { items, keys }] of groupedMap) {
      groups.push({
        key: groupKey,
        name: keys.join(' → '),
        startIndex,
        count: items.length,
        level: 0,
      });

      groupedItems.push(...items);
      startIndex += items.length;
    }

    return { groupedItems, groups };
  }, [filteredData, groupByKeys]);

  const handleColumnClick = (_ev: React.MouseEvent<HTMLElement>, column: IColumn) => {
    setSortColumn(column.fieldName);
    if (sortColumn === column.fieldName) {
      setIsSortedDescending(!isSortedDescending);
    } else {
      setIsSortedDescending(false);
    }
  };

  const renderHeader = (colName: string, colKey: string) => {
    const isGrouped = groupByKeys.includes(colKey);
    return (
      <div style={columnHeaderContainerStyles}>
        {colName}
        <TooltipHost styles={tooltipHostStyles} content={`Group by ${colName}`}>
          <IconButton
            iconProps={{ iconName: 'GroupList' }}
            styles={groupIconStyle(isGrouped, theme)}
            onClick={(e) => {
              e.stopPropagation();
              setGroupByKeys((prev) => {
                if (prev.includes(colKey)) {
                  return prev.filter((k) => k !== colKey);
                }
                return prev.length < 2 ? [...prev, colKey] : [colKey];
              });
            }}
          />
        </TooltipHost>
      </div>
    );
  };

  const columns = [
    {
      key: InstanceEventTableColKey.name,
      fieldName: InstanceEventTableColKey.name,
      name: InstanceEventTableColName.name,
      minWidth: 200,
      onRender: (item: InstanceEvent) => (
        <>{!isLoading ? item.instanceName : <SkeletonLoader width="65%" height="100%" />}</>
      ),
      styles: columnStyles(true),
      onRenderHeader: () => renderHeader(InstanceEventTableColName.name, InstanceEventTableColKey.name),
      isSorted: sortColumn === InstanceEventTableColKey.name,
      isSortedDescending: sortColumn === InstanceEventTableColKey.name && isSortedDescending,
      onColumnClick: handleColumnClick,
    },
    {
      key: InstanceEventTableColKey.event,
      fieldName: InstanceEventTableColKey.event,
      name: InstanceEventTableColName.event,
      minWidth: 200,
      onRender: (item: any) => {
        if (isLoading) return <SkeletonLoader width="65%" height="100%" />;
        const event = item.instanceEvent as InstanceEventName;
        const eventStyles = {
          [InstanceEventName.start]: startedEventStyles,
          [InstanceEventName.terminate]: terminatedEventStyles,
          [InstanceEventName.resume]: resumedEventStyles,
          [InstanceEventName.suspend]: suspendedEventStyles,
          [InstanceEventName.update]: updatedEventStyles,
        };
        return (
          <div>
            <Item
              data={{
                id: '',
                title: event,
              }}
              styles={eventStyles[event] || {}}
              idField="id"
              labelField="title"
            />
          </div>
        );
      },
      styles: columnStyles(true),
      onRenderHeader: () => renderHeader(InstanceEventTableColName.event, InstanceEventTableColKey.event),
      isSorted: sortColumn === InstanceEventTableColKey.event,
      isSortedDescending: sortColumn === InstanceEventTableColKey.event && isSortedDescending,
      onColumnClick: handleColumnClick,
    },
    {
      key: InstanceEventTableColKey.user,
      fieldName: InstanceEventTableColKey.user,
      name: InstanceEventTableColName.user,
      minWidth: 200,
      onRender: (item: InstanceEvent) => (
        <>{!isLoading ? item.instanceUser : <SkeletonLoader width="65%" height="100%" />}</>
      ),
      styles: columnStyles(true),
      onRenderHeader: () => renderHeader(InstanceEventTableColName.user, InstanceEventTableColKey.user),
      isSorted: sortColumn === InstanceEventTableColKey.user,
      isSortedDescending: sortColumn === InstanceEventTableColKey.user && isSortedDescending,
      onColumnClick: handleColumnClick,
    },
    {
      key: InstanceEventTableColKey.date,
      fieldName: InstanceEventTableColKey.date,
      name: InstanceEventTableColName.date,
      minWidth: 200,
      onRender: (item: InstanceEvent) => (
        <>{!isLoading ? item.instanceDate : <SkeletonLoader width="65%" height="100%" />}</>
      ),
      styles: columnStyles(true),
      onRenderHeader: () => renderHeader(InstanceEventTableColName.date, InstanceEventTableColKey.date),
      isSorted: sortColumn === InstanceEventTableColKey.date,
      isSortedDescending: sortColumn === InstanceEventTableColKey.date && isSortedDescending,
      onColumnClick: handleColumnClick,
    },
    {
      key: InstanceEventTableColKey.time,
      fieldName: InstanceEventTableColKey.time,
      name: InstanceEventTableColName.time,
      minWidth: 200,
      onRender: (item: InstanceEvent) => (
        <>{!isLoading ? item.instanceTime : <SkeletonLoader width="65%" height="100%" />}</>
      ),
      isSorted: sortColumn === InstanceEventTableColKey.time,
      isSortedDescending: sortColumn === InstanceEventTableColKey.time && isSortedDescending,
      onColumnClick: handleColumnClick,
      styles: columnStyles(true),
    },
  ];

  return (
    <div style={tableStyle(theme)}>
      {!isLoading && instanceEventData.length === 0 ? (
        <MessageBar messageBarType={MessageBarType.warning}>
          {'No data available for the selected date range'}
        </MessageBar>
      ) : (
        <DetailsList
          columns={columns}
          styles={customTableStyles(theme, '436')}
          items={isLoading ? dummyList : groupedItems}
          groups={isLoading ? undefined : groups}
        />
      )}
    </div>
  );
}
